import AddIcon from "@mui/icons-material/Add";
import { Box, IconButton, Typography } from "@mui/material";
import { FieldArray, getIn, useFormikContext } from "formik";
import { Fragment, useRef } from "react";
import type { AddAnotherProps, SolutionImpactFormValues } from "../../types";

function AddAnother<Value>({
  name,
  addLabel,
  getCounter,
  createValue,
  renderItem,
  compareValues,
  initialCounter = 0,
  beforeAddContent,
}: AddAnotherProps<Value>) {
  const { values: formValues } = useFormikContext<SolutionImpactFormValues>();
  const values = getIn(formValues, name) as Array<Value>;
  const indexedValues = values.map((value, index) => ({ value, index }));
  const displayedValues = compareValues
    ? [...indexedValues].sort((firstItem, secondItem) =>
        compareValues(firstItem.value, secondItem.value),
      )
    : indexedValues;

  const latestCounter = useRef(
    Math.max(initialCounter, ...values.map(getCounter)),
  );

  const getNextCounter = (): number => {
    latestCounter.current =
      Math.max(
        latestCounter.current,
        initialCounter,
        ...values.map(getCounter),
      ) + 1;
    return latestCounter.current;
  };

  return (
    <FieldArray name={name}>
      {({ push, remove }) => (
        <>
          {displayedValues.map(({ value, index }) => (
            <Fragment key={getCounter(value)}>
              {renderItem({
                value,
                index,
                remove: () => remove(index),
              })}
            </Fragment>
          ))}
          {beforeAddContent}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <IconButton
              onClick={() => push(createValue(getNextCounter(), values))}
              sx={{
                paddingTop: 1,
                "&:hover": { backgroundColor: "transparent" },
              }}
              aria-label={`add-${name}-button`}
              data-testid={`add-${name}-button`}
              size="large"
            >
              <AddIcon />
              <Typography variant="body2">{addLabel}</Typography>
            </IconButton>
          </Box>
        </>
      )}
    </FieldArray>
  );
}

export default AddAnother;
